import { useEffect, useState, useMemo } from 'react'
import { Users, Building2, ChevronRight, Search, X } from 'lucide-react'
import { serviceApi } from '../../services/api'
import { emotionZone, percentileZone } from '../../utils/emotionZone'
import { Avatar } from '../patterns/Avatar'
import Sparkline from './Sparkline'
import MetricDrawer from './MetricDrawer'
import StressConnections from './StressConnections'
import FeedbackRanking from './FeedbackRanking'
import ActorInteractionsDrawer from './ActorInteractionsDrawer'
import EventsFeed from './EventsFeed'

// ── Types ──────────────────────────────────────────────────────────────────────
interface DailySeries {
  date: string
  index: number
}

interface Card {
  id: string
  kind: 'incident' | 'trend'
  party: 'employee' | 'client'
  polarity: 'positive' | 'negative'
  severity: 'info' | 'warn' | 'critical'
  title: string
  hook: string
  source: string
  whenIso: string
}
interface PulseData {
  summary: { negativeSpikes: number; positiveSpikes: number; employeesAtRisk: number; clientsAtRisk: number }
  employees: Card[]
  clients: Card[]
  lastRunAt: string | null
  windowDays: number
  dailyNegative?: DailySeries[]
  dailyPositive?: DailySeries[]
}
interface TeamPerson {
  personRef: string
  displayName: string
  avatarUrl?: string | null
  index: number
  posSpikes: number
  negSpikes: number
  depthMax: number
  lastAt: string | null
  departmentId?: string | null
  companyId?: string | null
  sampleSize?: number
  confident?: boolean
  percentile?: number | null
}
interface TeamIndexData {
  companyIndex: number
  windowDays: number
  people: TeamPerson[]
  daily?: DailySeries[]
}
interface DeptIndex {
  departmentId: string
  name: string
  parentId: string | null
  index: number
  posSpikes: number
  negSpikes: number
  memberCount: number
  percentile?: number | null
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TEAM_CAP = 6
const DEPT_CAP = 6
const QS = 'window=30'

// ── Sub-components ─────────────────────────────────────────────────────────────
interface MetricCardProps {
  label: string
  value: number
  tone?: string
  series?: DailySeries[]
  seriesMode?: 'line' | 'bar'
  seriesColor?: string
  onClick?: () => void
}

function MetricCard({ label, value, tone, series, seriesMode = 'line', seriesColor = 'currentColor', onClick }: MetricCardProps) {
  const base = 'rounded-lg ring-1 ring-border bg-card px-3 py-2.5'
  const interactive = onClick ? 'cursor-pointer hover:bg-muted transition duration-150' : ''
  const content = (
    <>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold leading-tight ${tone ?? 'text-foreground'}`}>{value}</p>
      {series && series.length > 0 && (
        <div className="mt-1.5 opacity-70">
          <Sparkline points={series} mode={seriesMode} color={seriesColor} height={16} />
        </div>
      )}
    </>
  )
  if (onClick) {
    return (
      <button onClick={onClick} className={`${base} ${interactive} w-full text-left`}>
        {content}
      </button>
    )
  }
  return <div className={base}>{content}</div>
}

function IndexBar({ index, zone: zoneProp }: { index: number; zone?: { bar: string; text: string } }) {
  const zone = zoneProp ?? emotionZone(index)
  return (
    <span className="block h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <span className={`block h-full rounded-full ${zone.bar}`} style={{ width: `${Math.max(0, Math.min(100, index))}%` }} />
    </span>
  )
}

function TeamRow({ p, onOpen, relative }: { p: TeamPerson; onOpen: (ref: string, name: string) => void; relative: boolean }) {
  const lowData = p.confident === false
  const zone = lowData
    ? { bar: 'bg-muted-foreground/30', text: 'text-muted-foreground' }
    : (relative && typeof p.percentile === 'number' ? percentileZone(p.percentile) : emotionZone(p.index))
  return (
    <button onClick={() => onOpen(p.personRef, p.displayName ?? p.personRef)} className="w-full text-left flex items-center gap-3 px-2 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition duration-150" title={lowData ? 'low data' : undefined}>
      <Avatar name={p.displayName} src={p.avatarUrl} className="h-6 w-6 text-[10px] shrink-0" />
      <span className="w-28 shrink-0 truncate text-sm text-foreground">{p.displayName}</span>
      <span className="flex-1 min-w-0"><IndexBar index={p.index} zone={zone} /></span>
      <span className={`w-8 shrink-0 text-right text-sm font-medium tabular-nums ${zone.text}`}>{lowData ? '—' : p.index}</span>
      <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
        {p.negSpikes > 0 ? <span className="text-destructive">▼{p.negSpikes}</span> : <span className="text-emerald-500">▲{p.posSpikes}</span>}
      </span>
    </button>
  )
}

function DeptRow({ d, onFilter, relative }: { d: DeptIndex; onFilter: (d: DeptIndex) => void; relative: boolean }) {
  const zone = relative && typeof d.percentile === 'number' ? percentileZone(d.percentile) : emotionZone(d.index)
  return (
    <button onClick={() => onFilter(d)} className="w-full text-left flex items-center gap-3 px-2 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition duration-150">
      <span className="w-32 shrink-0 truncate text-sm text-foreground">{d.name}</span>
      <span className="flex-1 min-w-0"><IndexBar index={d.index} zone={zone} /></span>
      <span className={`w-8 shrink-0 text-right text-sm font-medium tabular-nums ${zone.text}`}>{d.index}</span>
      <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
        {d.negSpikes > 0 ? <span className="text-destructive">▼{d.negSpikes}</span> : <span className="text-emerald-500">▲{d.posSpikes}</span>}
      </span>
    </button>
  )
}

// ── SectionView ────────────────────────────────────────────────────────────────
export default function SectionView({ kind, relative }: { kind: 'employees' | 'clients' | 'partners'; relative: boolean }) {
  const [actorDrawer, setActorDrawer] = useState<{ ref: string; name: string } | null>(null)

  const [pulse, setPulse] = useState<PulseData | null>(null)
  const [team, setTeam] = useState<TeamIndexData | null>(null)
  const [departments, setDepartments] = useState<DeptIndex[]>([])
  const [clients, setClients] = useState<{ people: TeamPerson[]; daily?: DailySeries[] } | null>(null)
  const [companies, setCompanies] = useState<DeptIndex[]>([])

  const [showAllTeam, setShowAllTeam] = useState(false)
  const [showAllDepts, setShowAllDepts] = useState(false)
  const [query, setQuery] = useState('')
  const [deptFilter, setDeptFilter] = useState<{ id: string; name: string } | null>(null)
  const [companyFilter, setCompanyFilter] = useState<{ id: string; name: string } | null>(null)

  const [drawer, setDrawer] = useState<{
    title: string
    value: number
    series?: DailySeries[]
    seriesMode?: 'line' | 'bar'
    seriesColor?: string
    contributors?: { name: string; value: number | string }[]
  } | null>(null)

  useEffect(() => {
    if (kind === 'employees' || kind === 'clients' || kind === 'partners') {
      serviceApi.pulse(QS).then((d) => setPulse(d)).catch(() => setPulse(null))
    }
    if (kind === 'employees') {
      serviceApi.teamIndex(QS).then((d) => setTeam(d)).catch(() => setTeam(null))
      serviceApi.departmentIndex(QS).then((d) => setDepartments(d.departments)).catch(() => setDepartments([]))
    }
    const mapCompany = (c: { companyId: string; name: string; index: number; posSpikes: number; negSpikes: number; memberCount: number }): DeptIndex => ({
      departmentId: c.companyId,
      name: c.name,
      parentId: null,
      index: c.index,
      posSpikes: c.posSpikes,
      negSpikes: c.negSpikes,
      memberCount: c.memberCount,
    })
    if (kind === 'clients') {
      serviceApi.clientsIndex(QS).then((d) => setClients(d)).catch(() => setClients(null))
      serviceApi.companiesIndex(QS)
        .then((d) => setCompanies((d.companies ?? []).map(mapCompany)))
        .catch(() => setCompanies([]))
    }
    if (kind === 'partners') {
      serviceApi.partnersIndex(QS).then((d) => setClients(d)).catch(() => setClients(null))
      serviceApi.partnerCompaniesIndex(QS)
        .then((d) => setCompanies((d.companies ?? []).map(mapCompany)))
        .catch(() => setCompanies([]))
    }
  }, [kind])

  // OSPROD-233: clicking a leaderboard / feedback row must OPEN THE INTERACTION
  // DETAIL for that actor in-place, not navigate away to /users/:id or the CRM
  // section (QA: "переадресовывает на другой раздел"). Open the drawer instead.
  const openActor = (ref: string, name: string) => setActorDrawer({ ref, name })

  const teamPeople = useMemo(() => {
    let list = team?.people ?? []
    if (deptFilter) list = list.filter((p) => p.departmentId === deptFilter.id)
    if (query.trim()) { const q = query.toLowerCase(); list = list.filter((p) => (p.displayName ?? '').toLowerCase().includes(q)) }
    return list
  }, [team, deptFilter, query])

  const clientPeople = useMemo(() => {
    let list = clients?.people ?? []
    if (companyFilter) list = list.filter((p) => p.companyId === companyFilter.id)
    if (query.trim()) { const q = query.toLowerCase(); list = list.filter((p) => (p.displayName ?? '').toLowerCase().includes(q)) }
    return list
  }, [clients, companyFilter, query])

  const searchBox = (
    <div className="relative mb-2">
      <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
        className="w-full rounded-md bg-muted pl-7 pr-2 py-1 text-xs text-foreground border border-border" />
    </div>
  )

  // Label set for clients vs partners (used in leaderboards + metric row)
  const cpLabel = kind === 'partners' ? 'partners' : 'clients'
  const cpCompanyLabel = kind === 'partners' ? 'partner companies' : 'client companies'

  const employeeLeaderboards = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <Users size={16} />Team
        </h2>
        {searchBox}
        {deptFilter && (
          <button onClick={() => setDeptFilter(null)} className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-xs px-2 py-0.5">
            {deptFilter.name} <X size={11} />
          </button>
        )}
        <div className="flex flex-col gap-1">
          {(showAllTeam ? teamPeople : teamPeople.slice(0, TEAM_CAP)).map((p) => <TeamRow key={p.personRef} p={p} onOpen={openActor} relative={relative} />)}
          {team && teamPeople.length === 0 && <p className="py-2 text-sm text-muted-foreground">no people in range</p>}
        </div>
        {teamPeople.length > TEAM_CAP && (
          <button onClick={() => setShowAllTeam((v) => !v)} className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition duration-150">
            {showAllTeam ? 'show less' : `all employees (${teamPeople.length})`} <ChevronRight size={13} />
          </button>
        )}
      </section>
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <Building2 size={16} />Departments
        </h2>
        <div className="flex flex-col gap-1">
          {(showAllDepts ? departments : departments.slice(0, DEPT_CAP)).map((d) => <DeptRow key={d.departmentId} d={d} onFilter={(dd) => { setDeptFilter({ id: dd.departmentId, name: dd.name }); setShowAllTeam(true) }} relative={relative} />)}
          {departments.length === 0 && <p className="py-2 text-sm text-muted-foreground">no departments in range</p>}
        </div>
        {departments.length > DEPT_CAP && (
          <button onClick={() => setShowAllDepts((v) => !v)} className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition duration-150">
            {showAllDepts ? 'show less' : `all departments (${departments.length})`} <ChevronRight size={13} />
          </button>
        )}
      </section>
    </div>
  )

  const counterpartyLeaderboards = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <Users size={16} />{cpLabel.charAt(0).toUpperCase() + cpLabel.slice(1)}
        </h2>
        {searchBox}
        {companyFilter && (
          <button onClick={() => setCompanyFilter(null)} className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-xs px-2 py-0.5">
            {companyFilter.name} <X size={11} />
          </button>
        )}
        <div className="flex flex-col gap-1">
          {(showAllTeam ? clientPeople : clientPeople.slice(0, TEAM_CAP)).map((p) => <TeamRow key={p.personRef} p={p} onOpen={openActor} relative={relative} />)}
          {clients && clientPeople.length === 0 && <p className="py-2 text-sm text-muted-foreground">no {cpLabel} in range</p>}
        </div>
        {clientPeople.length > TEAM_CAP && (
          <button onClick={() => setShowAllTeam((v) => !v)} className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition duration-150">
            {showAllTeam ? 'show less' : `all ${cpLabel} (${clientPeople.length})`} <ChevronRight size={13} />
          </button>
        )}
      </section>
      <section className="rounded-lg ring-1 ring-border bg-card p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <Building2 size={16} />{cpCompanyLabel.charAt(0).toUpperCase() + cpCompanyLabel.slice(1)}
        </h2>
        <div className="flex flex-col gap-1">
          {(showAllDepts ? companies : companies.slice(0, DEPT_CAP)).map((d) => (
            <DeptRow key={d.departmentId} d={d} onFilter={(dd) => { setCompanyFilter({ id: dd.departmentId, name: dd.name }); setShowAllTeam(true) }} relative={relative} />
          ))}
          {companies.length === 0 && <p className="py-2 text-sm text-muted-foreground">no companies in range</p>}
        </div>
        {companies.length > DEPT_CAP && (
          <button onClick={() => setShowAllDepts((v) => !v)} className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition duration-150">
            {showAllDepts ? 'show less' : `all companies (${companies.length})`} <ChevronRight size={13} />
          </button>
        )}
      </section>
    </div>
  )

  // ── Metric cards ──────────────────────────────────────────────────────────
  // Clients/partners have no server-side company index yet (unlike team.companyIndex), so
  // the headline is the rounded mean of the loaded people indices; neutral 50 when empty.
  const counterpartyIndexValue = clientPeople.length
    ? Math.round(clientPeople.reduce((a, p) => a + p.index, 0) / clientPeople.length)
    : 50

  // Helpers to open the drawer from a card click
  const openIndexDrawer = () => {
    if (kind === 'employees') {
      const v = team?.companyIndex ?? 0
      setDrawer({
        title: 'Company index',
        value: v,
        series: team?.daily,
        seriesMode: 'line',
        seriesColor: emotionZone(v).text.replace('text-', '').startsWith('emerald')
          ? '#10b981'
          : emotionZone(v).text.includes('destructive')
            ? '#ef4444'
            : '#f59e0b',
        contributors: teamPeople.slice(0, 10).map((p) => ({ name: p.displayName, value: p.index })),
      })
    } else {
      setDrawer({
        title: cpLabel.charAt(0).toUpperCase() + cpLabel.slice(1) + ' index',
        value: counterpartyIndexValue,
        series: clients?.daily,
        seriesMode: 'line',
        seriesColor: '#6366f1',
        contributors: clientPeople.slice(0, 10).map((p) => ({ name: p.displayName, value: p.index })),
      })
    }
  }

  const openNegSpikesDrawer = () => {
    const cards = kind === 'employees' ? pulse?.employees ?? [] : pulse?.clients ?? []
    const negCards = cards.filter((c) => c.polarity === 'negative').slice(0, 10)
    setDrawer({
      title: 'Negative spikes',
      value: pulse?.summary.negativeSpikes ?? 0,
      series: pulse?.dailyNegative,
      seriesMode: 'bar',
      seriesColor: '#ef4444',
      contributors: negCards.map((c) => ({ name: c.title, value: c.hook })),
    })
  }

  const openPosSpikesDrawer = () => {
    const cards = kind === 'employees' ? pulse?.employees ?? [] : pulse?.clients ?? []
    const posCards = cards.filter((c) => c.polarity === 'positive').slice(0, 10)
    setDrawer({
      title: 'Positive spikes',
      value: pulse?.summary.positiveSpikes ?? 0,
      series: pulse?.dailyPositive,
      seriesMode: 'bar',
      seriesColor: '#10b981',
      contributors: posCards.map((c) => ({ name: c.title, value: c.hook })),
    })
  }

  const openAtRiskDrawer = () => {
    const atRisk = kind === 'employees'
      ? teamPeople.filter((p) => p.index < 40).slice(0, 10)
      : clientPeople.filter((p) => p.index < 40).slice(0, 10)
    setDrawer({
      title: kind === 'employees' ? 'People at risk' : cpLabel.charAt(0).toUpperCase() + cpLabel.slice(1) + ' at risk',
      value: kind === 'employees' ? (pulse?.summary.employeesAtRisk ?? 0) : (pulse?.summary.clientsAtRisk ?? 0),
      series: pulse?.dailyNegative,
      seriesMode: 'bar',
      seriesColor: '#f59e0b',
      contributors: atRisk.map((p) => ({ name: p.displayName, value: p.index })),
    })
  }

  const metricRow = kind === 'employees' ? (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard
        label="company index"
        value={team?.companyIndex ?? 0}
        tone={team ? emotionZone(team.companyIndex).text : undefined}
        series={team?.daily}
        seriesMode="line"
        seriesColor="currentColor"
        onClick={openIndexDrawer}
      />
      <MetricCard
        label="negative spikes"
        value={pulse?.summary.negativeSpikes ?? 0}
        tone="text-destructive"
        series={pulse?.dailyNegative}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openNegSpikesDrawer}
      />
      <MetricCard
        label="positive spikes"
        value={pulse?.summary.positiveSpikes ?? 0}
        tone="text-emerald-500"
        series={pulse?.dailyPositive}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openPosSpikesDrawer}
      />
      <MetricCard
        label="people at risk"
        value={pulse?.summary.employeesAtRisk ?? 0}
        series={pulse?.dailyNegative}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openAtRiskDrawer}
      />
    </div>
  ) : (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard
        label={`${cpLabel} index`}
        value={counterpartyIndexValue}
        tone={emotionZone(counterpartyIndexValue).text}
        series={clients?.daily}
        seriesMode="line"
        seriesColor="currentColor"
        onClick={openIndexDrawer}
      />
      <MetricCard
        label="negative spikes"
        value={pulse?.summary.negativeSpikes ?? 0}
        tone="text-destructive"
        series={pulse?.dailyNegative}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openNegSpikesDrawer}
      />
      <MetricCard
        label="positive spikes"
        value={pulse?.summary.positiveSpikes ?? 0}
        tone="text-emerald-500"
        series={pulse?.dailyPositive}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openPosSpikesDrawer}
      />
      <MetricCard
        label={`${cpLabel} at risk`}
        value={pulse?.summary.clientsAtRisk ?? 0}
        tone="text-amber-500"
        series={pulse?.dailyNegative}
        seriesMode="bar"
        seriesColor="currentColor"
        onClick={openAtRiskDrawer}
      />
    </div>
  )

  const apiKind: 'employee' | 'client' | 'partner' = kind === 'clients' ? 'client' : kind === 'partners' ? 'partner' : 'employee'

  return (
    <div className="flex flex-col gap-4">
      {metricRow}
      {kind === 'employees' ? employeeLeaderboards : counterpartyLeaderboards}
      <StressConnections kind={apiKind} />
      <FeedbackRanking kind={apiKind} onOpen={openActor} />
      <EventsFeed kind={apiKind} />
      <MetricDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.title ?? ''}
        value={drawer?.value ?? 0}
        series={drawer?.series}
        seriesMode={drawer?.seriesMode}
        seriesColor={drawer?.seriesColor}
        contributors={drawer?.contributors}
      />
      {actorDrawer && (
        <ActorInteractionsDrawer
          actor={actorDrawer}
          kind={apiKind}
          onClose={() => setActorDrawer(null)}
        />
      )}
    </div>
  )
}
